/**
 * KB-META
 * Purpose: Assistant knowledge service
 * Role: ui.service
 * EntryPoints: constructor(), if(), catch(), forEach()
 * Inputs: private http:HttpClient; selectedPages.length === 0:unknown; error:unknown; page => {
      if (!grouped[page.category]:unknown
 * Outputs: 
 * Validation: 
 * Dependencies: HttpClient
 * Contracts: 
 * Auth&Privacy: auth:none; hipaa:none; pii:none
 * Observability: 
 * Router: shard=partner-portal-cura; H2=services/assistant-knowledge.service; tags=ui
 * Owner: ; LastReviewed: 2025-08-25
 */
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

export interface PageKnowledge {
  id: string;
  name: string;
  route: string;
  category: string;
  complexity: string;
  description: string;
  version: string;
  lastUpdated: string;
  businessLogic: {
    primaryPurpose: string;
    keyWorkflows: string[];
    innovationOpportunities: string[];
    currentLimitations: string[];
    integrationPoints: string[];
    [key: string]: any;
  };
  [key: string]: any;
}

export interface SelectablePage {
  id: string;
  name: string;
  route: string;
  category: string;
  complexity: string;
  description: string;
  available: boolean;
  priority: number;
  estimatedTokens?: number;
}

export interface KnowledgeIndex {
  version: string;
  lastUpdated: string;
  description: string;
  categories: { [key: string]: string[] };
  pages: SelectablePage[];
  maxSelectable: number;
  totalAvailablePages: number;
}

@Injectable({
  providedIn: 'root'
})
export class AssistantKnowledgeService {
  private knowledgeCache = new Map<string, PageKnowledge>();
  private indexCache: KnowledgeIndex | null = null;
  private readonly basePath = '/assets/assistant-knowledge';

  constructor(private http: HttpClient) {}

  /**
   * Load knowledge for a specific page
   */
  async loadPageKnowledge(pageId: string): Promise<PageKnowledge | null> {
    if (this.knowledgeCache.has(pageId)) {
      return this.knowledgeCache.get(pageId)!;
    }

    try {
      const response = await this.http.get<PageKnowledge>(`${this.basePath}/pages/${pageId}.json`).toPromise();
      if (response) {
        this.knowledgeCache.set(pageId, response);
        return response;
      }
      return null;
    } catch (error) {
      console.warn(`Knowledge not found for page: ${pageId}`, error);
      return null;
    }
  }

  /**
   * Get list of all available pages
   */
  async getAvailablePages(): Promise<SelectablePage[]> {
    try {
      if (this.indexCache) {
        return this.indexCache.pages.filter(page => page.available);
      }

      const index = await this.http.get<KnowledgeIndex>(`${this.basePath}/index.json`).toPromise();
      if (index) {
        this.indexCache = index;
        return index.pages.filter(page => page.available);
      }
      return [];
    } catch (error) {
      console.error('Failed to load available pages', error);
      return [];
    }
  }

  /**
   * Get pages grouped by category
   */
  async getPagesByCategory(): Promise<{ [category: string]: SelectablePage[] }> {
    const pages = await this.getAvailablePages();
    const grouped: { [category: string]: SelectablePage[] } = {};

    pages.forEach(page => {
      if (!grouped[page.category]) {
        grouped[page.category] = [];
      }
      grouped[page.category].push(page);
    });

    // Sort pages within each category by priority
    Object.keys(grouped).forEach(category => {
      grouped[category].sort((a, b) => a.priority - b.priority);
    });

    return grouped;
  }

  /**
   * Build AI context for selected pages
   */
  async buildContextForPages(pageIds: string[]): Promise<string> {
    if (pageIds.length === 0) {
      return '';
    }

    const knowledgeItems = await Promise.all(
      pageIds.map(id => this.loadPageKnowledge(id))
    );

    const validKnowledge = knowledgeItems.filter(k => k !== null) as PageKnowledge[];

    if (validKnowledge.length === 0) {
      return '';
    }

    return this.formatKnowledgeForAI(validKnowledge);
  }

  /**
   * Format knowledge for AI consumption
   */
  private formatKnowledgeForAI(knowledgeItems: PageKnowledge[]): string {
    const totalTokens = knowledgeItems.reduce((sum, item) => {
      const pages = this.indexCache?.pages || [];
      const pageInfo = pages.find(p => p.id === item.id);
      return sum + (pageInfo?.estimatedTokens || 1000);
    }, 0);

    const formattedPages = knowledgeItems.map(item => this.formatPageContext(item)).join('\n\n');
    
    const crossPageOpportunities = this.generateCrossPageOpportunities(knowledgeItems);

    return `
PLATFORM CONTEXT FOR INNOVATION (${knowledgeItems.length} pages selected, ~${totalTokens} tokens):

${formattedPages}

CROSS-PAGE INNOVATION OPPORTUNITIES:
${crossPageOpportunities}

INNOVATION GUIDANCE:
When suggesting innovations, please:
1. Reference specific workflows, limitations, and integration points from the selected pages
2. Propose concrete solutions that build upon existing platform capabilities
3. Consider the technical architecture and current limitations mentioned
4. Suggest phased implementation approaches when appropriate
5. Identify synergies between the selected platform areas
6. Focus on measurable business outcomes and user experience improvements
`;
  }

  /**
   * Format individual page context
   */
  private formatPageContext(item: PageKnowledge): string {
    return `=== ${item.name.toUpperCase()} ===
Route: ${item.route}
Category: ${item.category} | Complexity: ${item.complexity}

PURPOSE: ${item.businessLogic.primaryPurpose}

KEY WORKFLOWS:
${item.businessLogic.keyWorkflows.map(w => `• ${w}`).join('\n')}

CURRENT LIMITATIONS:
${item.businessLogic.currentLimitations.map(l => `• ${l}`).join('\n')}

INNOVATION OPPORTUNITIES:
${item.businessLogic.innovationOpportunities.slice(0, 8).map(o => `• ${o}`).join('\n')}
${item.businessLogic.innovationOpportunities.length > 8 ? `... and ${item.businessLogic.innovationOpportunities.length - 8} more opportunities` : ''}

INTEGRATION POINTS:
${item.businessLogic.integrationPoints.map(i => `• ${i}`).join('\n')}`;
  }

  /**
   * Generate cross-page innovation opportunities
   */
  private generateCrossPageOpportunities(knowledgeItems: PageKnowledge[]): string {
    if (knowledgeItems.length < 2) {
      return '• Single page selected - consider adding related pages for cross-workflow innovation opportunities';
    }

    const opportunities: string[] = [];
    const pageNames = knowledgeItems.map(k => k.name);

    // Identify common themes across pages
    const allWorkflows = knowledgeItems.flatMap(k => k.businessLogic.keyWorkflows);
    const allLimitations = knowledgeItems.flatMap(k => k.businessLogic.currentLimitations);
    const allIntegrations = knowledgeItems.flatMap(k => k.businessLogic.integrationPoints);

    // Generate specific cross-page opportunities
    opportunities.push(`• Unified data flow optimization across ${pageNames.join(', ')}`);
    opportunities.push(`• Shared user interface patterns and navigation consistency`);
    opportunities.push(`• Cross-workflow automation between selected platform areas`);
    
    if (knowledgeItems.some(k => k.businessLogic.keyWorkflows.some(w => w.includes('communication')))) {
      opportunities.push(`• Unified communication platform integrating messaging across all selected workflows`);
    }

    if (knowledgeItems.some(k => k.businessLogic.keyWorkflows.some(w => w.includes('patient')))) {
      opportunities.push(`• Patient-centric dashboard combining insights from all selected patient touchpoints`);
    }

    if (allIntegrations.some(i => i.includes('API') || i.includes('system'))) {
      opportunities.push(`• API standardization and microservices architecture for better integration`);
    }

    // Add analytics opportunity if multiple pages selected
    opportunities.push(`• Advanced analytics combining data from all ${knowledgeItems.length} selected platform areas`);
    opportunities.push(`• Machine learning insights leveraging cross-platform user behavior patterns`);

    return opportunities.join('\n');
  }

  /**
   * Get knowledge statistics
   */
  async getKnowledgeStats(): Promise<{
    totalPages: number;
    totalCategories: number;
    lastUpdated: string;
    averageComplexity: string;
  }> {
    const pages = await this.getAvailablePages();
    const categories = new Set(pages.map(p => p.category));
    
    const complexityScores = { 'Simple': 1, 'Medium': 2, 'Complex': 3 };
    const avgComplexity = pages.reduce((sum, p) => sum + (complexityScores[p.complexity as keyof typeof complexityScores] || 2), 0) / pages.length;
    const avgComplexityLabel = avgComplexity < 1.5 ? 'Simple' : avgComplexity < 2.5 ? 'Medium' : 'Complex';

    return {
      totalPages: pages.length,
      totalCategories: categories.size,
      lastUpdated: this.indexCache?.lastUpdated || 'Unknown',
      averageComplexity: avgComplexityLabel
    };
  }

  /**
   * Search pages by keyword
   */
  async searchPages(keyword: string): Promise<SelectablePage[]> {
    const pages = await this.getAvailablePages();
    const searchTerm = keyword.toLowerCase();

    return pages.filter(page => 
      page.name.toLowerCase().includes(searchTerm) ||
      page.description.toLowerCase().includes(searchTerm) ||
      page.category.toLowerCase().includes(searchTerm)
    );
  }

  /**
   * Get recommended pages based on current selection
   */
  async getRecommendedPages(selectedPageIds: string[]): Promise<SelectablePage[]> {
    const allPages = await this.getAvailablePages();
    const selectedPages = allPages.filter(p => selectedPageIds.includes(p.id));
    
    if (selectedPages.length === 0) {
      // Return highest priority pages if nothing selected
      return allPages.sort((a, b) => a.priority - b.priority).slice(0, 3);
    }

    // Get pages from same categories as selected pages
    const selectedCategories = new Set(selectedPages.map(p => p.category));
    const sameCategoryPages = allPages.filter(p => 
      selectedCategories.has(p.category) && !selectedPageIds.includes(p.id)
    );

    // Get pages with related functionality (simple heuristic)
    const relatedPages = allPages.filter(p => {
      if (selectedPageIds.includes(p.id)) return false;
      return selectedPages.some(selected => 
        p.description.toLowerCase().includes('patient') && selected.description.toLowerCase().includes('patient') ||
        p.description.toLowerCase().includes('workflow') && selected.description.toLowerCase().includes('workflow')
      );
    });

    // Combine and deduplicate recommendations
    const recommendations = [...sameCategoryPages, ...relatedPages]
      .filter((page, index, arr) => arr.findIndex(p => p.id === page.id) === index)
      .sort((a, b) => a.priority - b.priority)
      .slice(0, 5);

    return recommendations;
  }

  /**
   * Clear knowledge cache
   */
  clearCache(): void {
    this.knowledgeCache.clear();
    this.indexCache = null;
  }
} 